import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Check, X, Trash2, TrendingUp, Eye, Users, Globe } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import VisitorsTab from "../components/admin/VisitorsTab";
import MarketingTab from "../components/admin/MarketingTab";
import CreditUnionTab from "../components/admin/CreditUnionTab";
import SurveyTab from "../components/admin/SurveyTab";
import OrganizersTab from "../components/admin/OrganizersTab";
import ReadinessTab from "../components/admin/ReadinessTab";
import RallyTab from "../components/admin/RallyTab";
import BoardTab from "../components/admin/BoardTab";
import FlyersTab from "../components/admin/FlyersTab";
import CapitalDonorsTab from "../components/admin/CapitalDonorsTab";

export default function AdminDashboard() {
  const [tab, setTab] = useState("analytics");

  return (
    <div className="grain min-h-screen max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="admin-page">
      <div className="label-eyebrow">Admin Console</div>
      <h1 className="mt-4 font-serif text-5xl text-bdu-cream tracking-tight">Manage The Fund</h1>

      <div className="mt-10 flex flex-wrap gap-x-1 gap-y-0 border-b border-bdu-gold/10">
        {[
          ["analytics", "Analytics"],
          ["visitors", "Visitors"],
          ["submissions", "Business Submissions"],
          ["events", "Events"],
          ["merch", "Merchandise"],
          ["news", "News Publishing"],
          ["pledges", "All Pledges"],
          ["payments", "Ticket Orders"],
          ["founders", "Founders Circle"],
          ["spotlight", "Spotlight"],
          ["marketing", "Marketing"],
          ["credit_union", "Credit Union"],
          ["readiness", "Charter Readiness"],
          ["capital_donors", "Capital Donors"],
          ["rally", "Rally RSVPs"],
          ["survey", "NCUA Survey"],
          ["organizers", "Organizers"],
          ["board", "Board & Committee"],
          ["flyers", "Flyers"],
          ["account", "Account"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-3 sm:px-4 py-3 text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === k ? "border-bdu-gold text-bdu-gold" : "border-transparent text-bdu-cream/60 hover:text-bdu-cream"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "visitors" && <VisitorsTab />}
        {tab === "submissions" && <BusinessesTab />}
        {tab === "events" && <EventsTab />}
        {tab === "merch" && <MerchTab />}
        {tab === "news" && <NewsTab />}
        {tab === "pledges" && <PledgesTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "founders" && <FoundersTab />}
        {tab === "spotlight" && <SpotlightTab />}
        {tab === "marketing" && <MarketingTab />}
        {tab === "credit_union" && <CreditUnionTab />}
        {tab === "readiness" && <ReadinessTab />}
        {tab === "capital_donors" && <CapitalDonorsTab />}
        {tab === "rally" && <RallyTab />}
        {tab === "survey" && <SurveyTab />}
        {tab === "organizers" && <OrganizersTab />}
        {tab === "board" && <BoardTab />}
        {tab === "flyers" && <FlyersTab />}
        {tab === "account" && <AccountTab />}
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/analytics/summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Failed to load analytics"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div className="text-bdu-cream/50 py-12 text-center">Loading analytics…</div>;
  if (err) return <div className="text-red-400 py-12 text-center" data-testid="analytics-error">{err}</div>;

  const daily = (data.daily || []).map((d) => ({ ...d, label: d.date.slice(5) })); // MM-DD

  return (
    <div className="space-y-10" data-testid="admin-analytics-tab">
      {/* Big stat tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatTile icon={<Eye size={18} />} label="Today · Views" value={data.today.views} sub={`${data.today.visitors} unique`} testId="stat-today" />
        <StatTile icon={<TrendingUp size={18} />} label="Last 7 Days · Views" value={data.week_7d.views} sub={`${data.week_7d.visitors} unique`} testId="stat-week" />
        <StatTile icon={<Users size={18} />} label="Last 30 Days · Views" value={data.month_30d.views} sub={`${data.month_30d.visitors} unique`} testId="stat-month" />
        <StatTile icon={<Globe size={18} />} label="All-Time Views" value={data.total_all_time} sub="Since launch" testId="stat-alltime" />
      </div>

      {/* Traffic chart */}
      <div className="border border-bdu-gold/20 bg-bdu-surface p-6" data-testid="analytics-chart">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Traffic · Last 30 Days</div>
            <div className="mt-1 text-bdu-cream/50 text-xs">Daily page views and unique visitors</div>
          </div>
          <button onClick={load} className="btn-ghost text-[10px]" data-testid="analytics-refresh">Refresh</button>
        </div>
        <div className="mt-6 h-72">
          {daily.length === 0 ? (
            <div className="h-full flex items-center justify-center text-bdu-cream/40 text-sm">
              No traffic yet — share your link to start seeing data here.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.08)" />
                <XAxis dataKey="label" tick={{ fill: "rgba(253,251,247,0.4)", fontSize: 11 }} axisLine={{ stroke: "rgba(212,175,55,0.1)" }} tickLine={false} />
                <YAxis tick={{ fill: "rgba(253,251,247,0.4)", fontSize: 11 }} axisLine={{ stroke: "rgba(212,175,55,0.1)" }} tickLine={false} width={35} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid rgba(212,175,55,0.3)", color: "#FDFBF7", fontSize: 12 }}
                  labelStyle={{ color: "#D4AF37" }}
                />
                <Area type="monotone" dataKey="views" stroke="#D4AF37" strokeWidth={2} fill="url(#viewsGrad)" name="Views" />
                <Area type="monotone" dataKey="visitors" stroke="#FDFBF7" strokeWidth={1.5} fill="transparent" strokeOpacity={0.5} name="Unique" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top pages + Top referrers */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-bdu-gold/20 bg-bdu-surface p-6" data-testid="analytics-top-pages">
          <div className="label-eyebrow">Top Pages · Last 30 Days</div>
          {data.top_pages.length === 0 ? (
            <div className="mt-6 text-bdu-cream/40 text-sm text-center py-8">No data yet</div>
          ) : (
            <ul className="mt-5 space-y-2">
              {data.top_pages.map((p, i) => {
                const max = data.top_pages[0].views || 1;
                const pct = Math.max(6, Math.round((p.views / max) * 100));
                return (
                  <li key={i} className="relative">
                    <div className="flex items-center justify-between gap-3 py-2 px-3 relative z-10">
                      <span className="text-bdu-cream text-sm truncate max-w-[70%]" title={p.path}>{p.path}</span>
                      <span className="text-bdu-gold text-sm tabular-nums whitespace-nowrap">{p.views.toLocaleString()} <span className="text-bdu-cream/40 text-xs">· {p.visitors} uniq</span></span>
                    </div>
                    <div className="absolute inset-y-0 left-0 bg-bdu-gold/8" style={{ width: `${pct}%` }} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border border-bdu-gold/20 bg-bdu-surface p-6" data-testid="analytics-top-refs">
          <div className="label-eyebrow">Top Traffic Sources · Last 30 Days</div>
          {data.top_referrers.length === 0 ? (
            <div className="mt-6 text-bdu-cream/40 text-sm text-center py-8">No external referrals yet.<br/>When someone clicks your link on IG, Facebook, or email, they'll show here.</div>
          ) : (
            <ul className="mt-5 space-y-2">
              {data.top_referrers.map((r, i) => {
                const max = data.top_referrers[0].views || 1;
                const pct = Math.max(6, Math.round((r.views / max) * 100));
                return (
                  <li key={i} className="relative">
                    <div className="flex items-center justify-between gap-3 py-2 px-3 relative z-10">
                      <span className="text-bdu-cream text-sm truncate max-w-[70%]">{r.referrer}</span>
                      <span className="text-bdu-gold text-sm tabular-nums">{r.views.toLocaleString()}</span>
                    </div>
                    <div className="absolute inset-y-0 left-0 bg-bdu-gold/8" style={{ width: `${pct}%` }} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <p className="text-xs text-bdu-cream/40 text-center pt-4">
        Analytics are stored in your own database — no third parties, no cookie banner needed. Admin views are excluded from tracking.
      </p>
    </div>
  );
}

function StatTile({ icon, label, value, sub, testId }) {
  return (
    <div className="border border-bdu-gold/20 bg-bdu-surface p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-bdu-gold">
        {icon}
        <span className="label-eyebrow" style={{ fontSize: "0.6rem" }}>{label}</span>
      </div>
      <div className="mt-4 font-serif text-4xl text-bdu-cream tabular-nums">{Number(value || 0).toLocaleString()}</div>
      <div className="mt-1 text-xs text-bdu-cream/50">{sub}</div>
    </div>
  );
}

function BusinessesTab() {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/businesses", { params: { status: filter } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/businesses/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteOne = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/businesses/${id}`);
      toast.success("Business deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const purgeTest = async () => {
    const testCount = items.filter((b) => (b.name || "").toUpperCase().startsWith("TEST_")).length;
    const confirm1 = window.prompt(
      `This will DELETE all businesses whose name starts with "TEST_" across ALL statuses.\n\nDetected in current view: ${testCount}\n\nType PURGE to confirm.`
    );
    if (confirm1 !== "PURGE") return;
    try {
      const r = await api.post("/admin/businesses/purge-test-data");
      toast.success(`Deleted ${r.data.deleted} test businesses`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to purge");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6 justify-between">
        <div className="flex gap-3">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`px-3 py-1.5 border text-xs uppercase tracking-widest ${
                filter === s ? "border-bdu-gold text-bdu-gold" : "border-bdu-gold/20 text-bdu-cream/60"
              }`}
            >{s}</button>
          ))}
        </div>
        <button
          onClick={purgeTest}
          data-testid="admin-purge-test-businesses-btn"
          className="px-4 py-1.5 border border-bdu-red/60 text-bdu-red text-xs uppercase tracking-widest hover:bg-bdu-red/10 transition"
        >
          Purge TEST_* Data
        </button>
      </div>
      {loading ? (
        <div className="text-bdu-cream/50 py-10 text-center">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bdu-card text-center py-10 text-bdu-cream/60">No submissions.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((b) => (
            <div key={b.id} className="bdu-card" data-testid={`admin-business-${b.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="label-eyebrow">{b.category}</div>
                  <h3 className="mt-2 font-serif text-2xl text-bdu-cream">{b.name}</h3>
                  <div className="text-xs text-bdu-cream/50 mt-1">
                    {b.address ? <>{b.address} · </> : null}{b.city}, {b.state}
                  </div>
                </div>
                <StatusPill status={b.status} />
              </div>
              <p className="mt-4 text-sm text-bdu-cream/70">{b.description}</p>
              <div className="mt-4 text-xs text-bdu-cream/40">Submitted by {b.submitted_by_name}</div>
              <div className="mt-5 flex gap-2 flex-wrap">
                {b.status !== "approved" && <button onClick={() => update(b.id, "approved")} className="btn-gold text-xs" data-testid={`approve-${b.id}`}><Check size={14}/>Approve</button>}
                {b.status !== "rejected" && <button onClick={() => update(b.id, "rejected")} className="btn-ghost text-xs" data-testid={`reject-${b.id}`}><X size={14}/>Reject</button>}
                <button
                  onClick={() => deleteOne(b.id, b.name)}
                  data-testid={`delete-business-${b.id}`}
                  className="px-3 py-2 text-xs uppercase tracking-widest text-bdu-red/80 hover:text-bdu-red border border-bdu-red/30 hover:border-bdu-red inline-flex items-center gap-1 transition"
                >
                  <Trash2 size={14}/>Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "border-bdu-gold/40 text-bdu-gold",
    approved: "border-emerald-500/40 text-emerald-400",
    rejected: "border-bdu-red/40 text-bdu-red",
  };
  return <span className={`text-[0.6rem] uppercase tracking-widest px-2 py-1 border ${map[status] || ""}`}>{status}</span>;
}

function NewsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", image_url: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/news").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const publish = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/news", form);
      toast.success("News published");
      setForm({ title: "", body: "", image_url: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/news/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={publish} className="bdu-card p-8 space-y-5" data-testid="admin-news-form">
        <h3 className="font-serif text-2xl text-bdu-cream">Publish an Update</h3>
        <div>
          <div className="label-eyebrow mb-3">Title</div>
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold"
            data-testid="admin-news-title-input" />
        </div>
        <div>
          <div className="label-eyebrow mb-3">Body</div>
          <textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold text-sm"
            data-testid="admin-news-body-input" />
        </div>
        <div>
          <div className="label-eyebrow mb-3">Image URL (optional)</div>
          <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            className="w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold"
            data-testid="admin-news-image-input" />
        </div>
        <button type="submit" disabled={submitting} className="btn-gold" data-testid="admin-news-submit-btn">
          {submitting ? "Publishing..." : "Publish"}
        </button>
      </form>

      <div className="space-y-4">
        <h3 className="font-serif text-2xl text-bdu-cream">Recent Posts</h3>
        {items.length === 0 ? (
          <div className="bdu-card text-bdu-cream/60">No posts yet.</div>
        ) : items.map((n) => (
          <div key={n.id} className="bdu-card" data-testid={`admin-news-item-${n.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>{new Date(n.created_at).toLocaleDateString()}</div>
                <h4 className="mt-2 font-serif text-xl text-bdu-cream">{n.title}</h4>
                <p className="mt-2 text-sm text-bdu-cream/60 line-clamp-3">{n.body}</p>
              </div>
              <button onClick={() => remove(n.id)} className="p-2 text-bdu-red/70 hover:text-bdu-red" data-testid={`admin-news-delete-${n.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsTab() {
  const emptyForm = {
    title: "", subtitle: "", description: "", event_date: "", location: "",
    status: "coming_soon", presented_by: "Black Dollars United",
    image_url: "", cta_label: "", cta_url: "",
    total_raised: "",
    highlights: [],
    testimonials: [],
  };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        total_raised: form.total_raised === "" || form.total_raised == null ? null : Number(form.total_raised),
        highlights: (form.highlights || []).filter((h) => h.image_url),
        testimonials: (form.testimonials || []).filter((t) => t.quote),
      };
      if (editingId) {
        await api.patch(`/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event published");
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const edit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "", subtitle: ev.subtitle || "", description: ev.description || "",
      event_date: ev.event_date || "", location: ev.location || "",
      status: ev.status || "coming_soon", presented_by: ev.presented_by || "Black Dollars United",
      image_url: ev.image_url || "", cta_label: ev.cta_label || "", cta_url: ev.cta_url || "",
      total_raised: ev.total_raised != null ? String(ev.total_raised) : "",
      highlights: Array.isArray(ev.highlights) ? ev.highlights : [],
      testimonials: Array.isArray(ev.testimonials) ? ev.testimonials : [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try { await api.delete(`/events/${id}`); toast.success("Event removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const inputCls = "w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold text-sm";

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-events-tab">
      <form onSubmit={save} className="bdu-card p-8 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-bdu-cream">{editingId ? "Edit Event" : "Create New Event"}</h3>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }} className="text-xs text-bdu-cream/60 hover:text-bdu-gold uppercase tracking-widest">Cancel edit</button>
          )}
        </div>
        <div><div className="label-eyebrow mb-3">Title</div>
          <input required value={form.title} onChange={update("title")} className={inputCls} data-testid="admin-event-title-input" /></div>
        <div><div className="label-eyebrow mb-3">Subtitle</div>
          <input value={form.subtitle} onChange={update("subtitle")} className={inputCls} data-testid="admin-event-subtitle-input" /></div>
        <div><div className="label-eyebrow mb-3">Description</div>
          <textarea required rows={5} value={form.description} onChange={update("description")} className={inputCls} data-testid="admin-event-description-input" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="label-eyebrow mb-3">Date</div>
            <input value={form.event_date} onChange={update("event_date")} placeholder="Fall 2026 · TBA" className={inputCls} data-testid="admin-event-date-input" /></div>
          <div><div className="label-eyebrow mb-3">Location</div>
            <input value={form.location} onChange={update("location")} className={inputCls} data-testid="admin-event-location-input" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="label-eyebrow mb-3">Status</div>
            <select value={form.status} onChange={update("status")} className={inputCls} data-testid="admin-event-status-input">
              <option value="coming_soon">Coming Soon</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select></div>
          <div><div className="label-eyebrow mb-3">Presented By</div>
            <input value={form.presented_by} onChange={update("presented_by")} className={inputCls} /></div>
        </div>
        <div><div className="label-eyebrow mb-3">Image URL</div>
          <input value={form.image_url} onChange={update("image_url")} placeholder="https://..." className={inputCls} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="label-eyebrow mb-3">CTA Label</div>
            <input value={form.cta_label} onChange={update("cta_label")} className={inputCls} /></div>
          <div><div className="label-eyebrow mb-3">CTA URL</div>
            <input value={form.cta_url} onChange={update("cta_url")} placeholder="/register or https://..." className={inputCls} /></div>
        </div>

        {/* Past highlights section (fill in after the event happens) */}
        <div className="pt-6 mt-2 border-t border-bdu-gold/15 space-y-5" data-testid="admin-event-highlights-editor">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Past Event Highlights</div>
              <p className="mt-1 text-xs text-bdu-cream/50">Fill these in after the event to display a gallery of photos, guest testimonials, and the total raised.</p>
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-3">Total Raised (USD)</div>
            <input
              type="number" min="0" step="1"
              value={form.total_raised}
              onChange={update("total_raised")}
              placeholder="e.g., 175000"
              className={inputCls}
              data-testid="admin-event-total-raised-input"
            />
          </div>

          {/* Gallery photos */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">Gallery Photos</div>
              <button type="button"
                onClick={() => setForm({ ...form, highlights: [...(form.highlights || []), { image_url: "", caption: "" }] })}
                className="text-xs uppercase tracking-widest text-bdu-gold hover:underline"
                data-testid="admin-event-add-highlight-btn"
              >+ Add Photo</button>
            </div>
            {(form.highlights || []).length === 0 && (
              <p className="text-xs text-bdu-cream/40 italic">No photos yet. Click "+ Add Photo" to add one.</p>
            )}
            {(form.highlights || []).map((h, i) => (
              <div key={i} className="mb-3 grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`admin-event-highlight-row-${i}`}>
                <input value={h.image_url}
                  onChange={(e) => {
                    const next = [...form.highlights];
                    next[i] = { ...next[i], image_url: e.target.value };
                    setForm({ ...form, highlights: next });
                  }}
                  placeholder="Image URL"
                  className={inputCls}
                  data-testid={`admin-event-highlight-url-${i}`}
                />
                <input value={h.caption || ""}
                  onChange={(e) => {
                    const next = [...form.highlights];
                    next[i] = { ...next[i], caption: e.target.value };
                    setForm({ ...form, highlights: next });
                  }}
                  placeholder="Caption (optional)"
                  className={inputCls}
                />
                <button type="button"
                  onClick={() => setForm({ ...form, highlights: form.highlights.filter((_, j) => j !== i) })}
                  className="text-bdu-red/70 hover:text-bdu-red px-3 py-2 border border-bdu-red/30 text-xs uppercase tracking-widest"
                  data-testid={`admin-event-remove-highlight-${i}`}
                >Remove</button>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">Testimonials</div>
              <button type="button"
                onClick={() => setForm({ ...form, testimonials: [...(form.testimonials || []), { quote: "", author: "", role: "" }] })}
                className="text-xs uppercase tracking-widest text-bdu-gold hover:underline"
                data-testid="admin-event-add-testimonial-btn"
              >+ Add Testimonial</button>
            </div>
            {(form.testimonials || []).length === 0 && (
              <p className="text-xs text-bdu-cream/40 italic">No testimonials yet.</p>
            )}
            {(form.testimonials || []).map((t, i) => (
              <div key={i} className="mb-4 border border-bdu-gold/10 p-4 space-y-2" data-testid={`admin-event-testimonial-row-${i}`}>
                <textarea rows={2} value={t.quote}
                  onChange={(e) => {
                    const next = [...form.testimonials];
                    next[i] = { ...next[i], quote: e.target.value };
                    setForm({ ...form, testimonials: next });
                  }}
                  placeholder="Guest quote..."
                  className={inputCls}
                  data-testid={`admin-event-testimonial-quote-${i}`}
                />
                <div className="grid sm:grid-cols-3 gap-2">
                  <input value={t.author || ""}
                    onChange={(e) => {
                      const next = [...form.testimonials];
                      next[i] = { ...next[i], author: e.target.value };
                      setForm({ ...form, testimonials: next });
                    }}
                    placeholder="Author name"
                    className={inputCls}
                  />
                  <input value={t.role || ""}
                    onChange={(e) => {
                      const next = [...form.testimonials];
                      next[i] = { ...next[i], role: e.target.value };
                      setForm({ ...form, testimonials: next });
                    }}
                    placeholder="Role / title (optional)"
                    className={inputCls}
                  />
                  <button type="button"
                    onClick={() => setForm({ ...form, testimonials: form.testimonials.filter((_, j) => j !== i) })}
                    className="text-bdu-red/70 hover:text-bdu-red px-3 py-2 border border-bdu-red/30 text-xs uppercase tracking-widest"
                    data-testid={`admin-event-remove-testimonial-${i}`}
                  >Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={submitting} className="btn-gold" data-testid="admin-event-submit-btn">
          {submitting ? "Saving..." : editingId ? "Save Changes" : "Publish Event"}
        </button>
      </form>

      <div className="space-y-4">
        <h3 className="font-serif text-2xl text-bdu-cream">All Events</h3>
        {items.length === 0 ? (
          <div className="bdu-card text-bdu-cream/60">No events yet.</div>
        ) : items.map((ev) => (
          <div key={ev.id} className="bdu-card" data-testid={`admin-event-item-${ev.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>{ev.status.replace("_", " ")}</div>
                <h4 className="mt-2 font-serif text-xl text-bdu-cream">{ev.title}</h4>
                {ev.subtitle && <div className="mt-1 italic text-bdu-gold/80 text-sm">{ev.subtitle}</div>}
                <p className="mt-2 text-xs text-bdu-cream/60 line-clamp-2">{ev.description}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => edit(ev)} className="text-xs uppercase tracking-widest text-bdu-gold hover:underline" data-testid={`admin-event-edit-${ev.id}`}>Edit</button>
                <button onClick={() => remove(ev.id)} className="text-xs uppercase tracking-widest text-bdu-red/80 hover:text-bdu-red" data-testid={`admin-event-delete-${ev.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ZeffyWebhookCard({ zeffy }) {
  const [copied, setCopied] = useState(false);
  const ready = !!zeffy?.webhook_ready;
  const url = zeffy?.webhook_url || "";
  const lastAt = zeffy?.last_webhook_at;
  const totalReceived = zeffy?.total_webhooks_received || 0;

  const copyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Webhook URL copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — long-press the URL to copy manually");
    }
  };

  const formatLast = (iso) => {
    if (!iso) return "never";
    try {
      const d = new Date(iso);
      const diff = Math.floor((Date.now() - d.getTime()) / 1000);
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch { return iso; }
  };

  return (
    <div className="border border-bdu-gold/20 bg-bdu-surface p-5 mb-6" data-testid="zeffy-webhook-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Zeffy · Real-Time Webhook</div>
          <div className="mt-1 text-sm text-bdu-cream/80">
            {!ready ? (
              <span>
                <span className="text-amber-400">● Not configured</span> — set{" "}
                <span className="font-mono text-bdu-gold">ZEFFY_WEBHOOK_SECRET</span> in your environment,
                then paste the generated URL into Zeffy → Settings → Integrations.
              </span>
            ) : totalReceived === 0 ? (
              <span>
                <span className="text-amber-400">● Waiting for first event</span> — copy the URL below into
                Zeffy → Settings → Integrations to enable real-time sync.
              </span>
            ) : (
              <span>
                <span className="text-emerald-400">● Live</span> — {totalReceived} event(s) received,
                last one <span className="text-bdu-gold">{formatLast(lastAt)}</span>
                {zeffy?.last_webhook_event ? <span className="text-bdu-cream/50"> · {zeffy.last_webhook_event}</span> : null}
              </span>
            )}
          </div>
        </div>
      </div>

      {ready && url && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <div
            className="flex-1 min-w-0 font-mono text-xs text-bdu-cream/70 bg-bdu-bg border border-bdu-gold/10 px-3 py-2 truncate select-all"
            data-testid="zeffy-webhook-url"
            title={url}
          >
            {url}
          </div>
          <button
            onClick={copyUrl}
            className="btn-gold text-xs whitespace-nowrap"
            data-testid="zeffy-webhook-copy-btn"
          >
            {copied ? "Copied ✓" : "Copy URL"}
          </button>
        </div>
      )}

      <div className="mt-3 text-xs text-bdu-cream/50 leading-relaxed">
        Zeffy will POST every completed donation to this URL. Pledges appear in the fund total instantly —
        no manual sync required. Handled events are logged and idempotent.
      </div>
    </div>
  );
}


function PledgesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zeffy, setZeffy] = useState(null);
  const [zSyncing, setZSyncing] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/admin/pledges").then((r) => setItems(r.data)).finally(() => setLoading(false));
    api.get("/admin/zeffy/status").then((r) => setZeffy(r.data)).catch(() => setZeffy({ configured: false }));
  };
  useEffect(() => { load(); }, []);

  const total = items.reduce((s, p) => s + Number(p.amount || 0), 0);
  const zeffyTotal = items.filter((p) => p.source === "zeffy").reduce((s, p) => s + Number(p.amount || 0), 0);
  const stripeTotal = total - zeffyTotal;

  const syncZeffy = async () => {
    setZSyncing(true);
    try {
      const { data } = await api.post("/admin/zeffy/sync");
      const msg = `Zeffy sync · imported ${data.imported}, updated ${data.updated}, total ${data.total_processed}`;
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Zeffy sync failed");
    } finally {
      setZSyncing(false);
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this pledge? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/pledges/${id}`);
      toast.success("Pledge deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const resetAll = async () => {
    const confirm1 = window.prompt(
      `This will DELETE ALL ${items.length} pledges permanently. Type RESET to confirm.`
    );
    if (confirm1 !== "RESET") return;
    try {
      const r = await api.post("/admin/pledges/reset");
      toast.success(`Cleared ${r.data.deleted} pledges`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset");
    }
  };

  return (
    <div>
      {/* Zeffy sync bar */}
      <div className="border border-bdu-gold/20 bg-bdu-surface p-5 flex items-center justify-between flex-wrap gap-3 mb-3" data-testid="zeffy-status-bar">
        <div className="min-w-0">
          <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Zeffy Donations · Sync</div>
          <div className="mt-1 text-sm text-bdu-cream/80">
            {zeffy === null ? "Checking connection…"
              : !zeffy.configured ? <span><span className="text-amber-400">● Not connected</span> — set <span className="font-mono text-bdu-gold">ZEFFY_API_KEY</span></span>
              : <span><span className="text-emerald-400">● Connected</span> — {zeffy.synced_donations} donation(s) synced totaling <span className="text-bdu-gold">${(zeffy.total_zeffy_dollars || 0).toLocaleString()}</span></span>
            }
          </div>
        </div>
        <button
          onClick={syncZeffy}
          disabled={zSyncing || !zeffy?.configured}
          className="btn-gold text-xs"
          data-testid="zeffy-sync-btn"
        >
          {zSyncing ? "Syncing…" : "Sync from Zeffy"}
        </button>
      </div>

      {/* Zeffy real-time webhook card */}
      <ZeffyWebhookCard zeffy={zeffy} />

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bdu-card">
          <div className="label-eyebrow">Total Fund (Stripe + Zeffy)</div>
          <div className="mt-2 font-serif text-4xl text-bdu-gold" data-testid="admin-total-amount">${total.toLocaleString()}</div>
          <div className="mt-3 flex gap-4 text-xs text-bdu-cream/60">
            <span>Stripe · <span className="text-bdu-cream">${stripeTotal.toLocaleString()}</span></span>
            <span>Zeffy · <span className="text-bdu-cream">${zeffyTotal.toLocaleString()}</span></span>
          </div>
        </div>
        <div className="bdu-card">
          <div className="label-eyebrow">Pledge Count</div>
          <div className="mt-2 font-serif text-4xl text-bdu-cream" data-testid="admin-pledge-count">{items.length}</div>
        </div>
        <div className="bdu-card">
          <div className="label-eyebrow">Goal Progress</div>
          <div className="mt-2 font-serif text-4xl text-bdu-cream">{((total / 50_000_000) * 100).toFixed(4)}%</div>
          <div className="mt-2 text-xs text-bdu-cream/50">of $50M / 2-year goal</div>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mb-6 flex justify-end">
          <button
            onClick={resetAll}
            data-testid="admin-reset-pledges-btn"
            className="px-4 py-2 border border-bdu-red/60 text-bdu-red hover:bg-bdu-red/10 uppercase tracking-widest text-xs transition"
          >
            Clear All Test Pledges
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-bdu-cream/50 py-10 text-center">Loading...</div>
      ) : items.length === 0 ? (
        <div className="border border-bdu-gold/10 py-16 text-center text-bdu-cream/50">
          No pledges yet. Real donor pledges will appear here.
        </div>
      ) : (
        <div className="border border-bdu-gold/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Member</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Note</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-bdu-gold/10">
                  <td className="px-6 py-4 text-bdu-cream/70">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-bdu-cream">{p.user_name}</td>
                  <td className="px-6 py-4 text-bdu-gold font-serif text-lg">${Number(p.amount).toLocaleString()}</td>
                  <td className="px-6 py-4 text-bdu-cream/60">{p.note || "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => deleteOne(p.id)}
                      data-testid={`admin-delete-pledge-${p.id}`}
                      className="text-bdu-red/80 hover:text-bdu-red text-xs uppercase tracking-widest"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


const TIER_LABEL = {
  general: "General",
  vip: "VIP",
  sponsor_table: "Sponsor Table",
  founding_sponsor: "Founding Sponsor",
};

function PaymentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/payments").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const paid = items.filter((p) => p.payment_status === "paid");
  const totalRevenueCents = paid.reduce((s, p) => s + Number(p.amount || 0), 0);

  const badge = (s) => {
    if (s === "paid") return "border-emerald-500/40 text-emerald-400";
    if (s === "pending") return "border-bdu-gold/40 text-bdu-gold";
    if (s === "expired" || s === "failed") return "border-bdu-red/40 text-bdu-red";
    return "border-bdu-cream/30 text-bdu-cream/60";
  };

  return (
    <div data-testid="admin-payments-tab">
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bdu-card">
          <div className="label-eyebrow">Orders Paid</div>
          <div className="mt-2 font-serif text-4xl text-bdu-cream" data-testid="admin-tickets-sold">{paid.length}</div>
        </div>
        <div className="bdu-card">
          <div className="label-eyebrow">Revenue (paid)</div>
          <div className="mt-2 font-serif text-4xl text-bdu-gold" data-testid="admin-tickets-revenue">
            ${(totalRevenueCents / 100).toLocaleString()}
          </div>
        </div>
        <div className="bdu-card">
          <div className="label-eyebrow">All Sessions</div>
          <div className="mt-2 font-serif text-4xl text-bdu-cream" data-testid="admin-tickets-total-sessions">{items.length}</div>
        </div>
      </div>
      {loading ? (
        <div className="text-bdu-cream/50 py-10 text-center">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bdu-card text-center py-10 text-bdu-cream/60">No ticket orders yet.</div>
      ) : (
        <div className="border border-bdu-gold/10 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Ticket</th>
                <th className="px-6 py-4 text-center">Qty</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-bdu-gold/10">
                  <td className="px-6 py-4 text-bdu-cream/70">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                  <td className="px-6 py-4 text-bdu-cream/80">{p.email || "—"}</td>
                  <td className="px-6 py-4 text-bdu-cream">
                    {p.product_type === "merch"
                      ? `${p.item_name || "Merch"}${p.size ? ` (${p.size})` : ""}`
                      : (TIER_LABEL[p.tier] || p.tier)}
                  </td>
                  <td className="px-6 py-4 text-center text-bdu-cream">{p.quantity}</td>
                  <td className="px-6 py-4 text-bdu-gold font-serif text-lg">${(Number(p.amount || 0) / 100).toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`text-[0.6rem] uppercase tracking-widest px-2 py-1 border ${badge(p.payment_status)}`}>
                      {p.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const MERCH_CATEGORIES = [
  { value: "tshirt", label: "T-Shirt / Apparel" },
  { value: "hat", label: "Hat / Headwear" },
  { value: "other", label: "Other" },
];

function MerchRepairButtons({ onRepaired }) {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const repair = async (params) => {
    const { reactivateAll = false, cleanupAutoimports = false } = params;
    let label = "Fix broken images";
    if (reactivateAll && cleanupAutoimports) label = "Restore + clean duplicates";
    else if (reactivateAll) label = "Restore & re-activate all";
    else if (cleanupAutoimports) label = "Clean Printful duplicates";

    if (reactivateAll && !window.confirm("Re-activate every hidden merch item on the shop?")) return;
    if (cleanupAutoimports && !window.confirm("Hide any items imported from Printful with 'Auto-imported from Printful' descriptions?")) return;

    setBusy(true);
    try {
      const { data } = await api.post("/admin/merchandise/repair", null, {
        params: { reactivate_all: reactivateAll, cleanup_autoimports: cleanupAutoimports },
      });
      setLastResult(data);
      const parts = [];
      if (data.image_urls_fixed) parts.push(`${data.image_urls_fixed} image URL(s) fixed`);
      if (data.r2p_palette_fixed) parts.push(`${data.r2p_palette_fixed} R2P mockup(s) swapped`);
      if (data.brand_field_backfilled) parts.push(`${data.brand_field_backfilled} brand(s) tagged`);
      if (data.reactivated) parts.push(`${data.reactivated} re-activated`);
      if (data.autoimports_hidden?.length) parts.push(`${data.autoimports_hidden.length} auto-import(s) hidden`);
      if (data.movement_sort_order_fixed) parts.push(`Movement sort_order fixed`);
      toast.success(parts.length ? `${label}: ${parts.join(", ")}` : `${label}: nothing to fix`);
      onRepaired && onRepaired();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Repair failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="merch-repair-toolbar">
      <button
        onClick={() => repair({})}
        disabled={busy}
        className="text-xs uppercase tracking-widest border border-bdu-gold/40 text-bdu-cream/80 hover:text-bdu-gold hover:border-bdu-gold px-3 py-2 disabled:opacity-50"
        title="Rewrite any image_url that points at the preview domain so it resolves on production"
        data-testid="merch-repair-images-btn"
      >
        {busy ? "Working…" : "🔧 Fix Broken Images"}
      </button>
      <button
        onClick={() => repair({ cleanupAutoimports: true })}
        disabled={busy}
        className="text-xs uppercase tracking-widest border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-3 py-2 disabled:opacity-50"
        title="Hide items whose descriptions start with 'Auto-imported from Printful' — usually accidental duplicates"
        data-testid="merch-clean-duplicates-btn"
      >
        {busy ? "Working…" : "🧹 Clean Duplicates"}
      </button>
      <button
        onClick={() => repair({ reactivateAll: true })}
        disabled={busy}
        className="text-xs uppercase tracking-widest border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-3 py-2 disabled:opacity-50"
        title="Fix images AND re-activate every hidden item"
        data-testid="merch-restore-all-btn"
      >
        {busy ? "Working…" : "🔄 Restore All"}
      </button>
      <button
        onClick={async () => {
          if (!window.confirm("Insert/refresh all curated special drops (Rise II Power Black Cap, BDU Original Logo Tee + Cap)?\n\nSafe to click on production — existing rows just get refreshed.")) return;
          setBusy(true);
          try {
            const { data } = await api.post("/admin/merchandise/seed_specials");
            const list = (data.seeded || []).map((r) => `${r.name.split("—")[1]?.trim() || r.name}: ${r.action}`).join(" · ");
            toast.success(list || "Seed complete");
            onRepaired && onRepaired();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Seed failed");
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy}
        className="text-xs uppercase tracking-widest border border-bdu-gold/60 text-bdu-gold hover:bg-bdu-gold/10 px-3 py-2 disabled:opacity-50"
        title="Seed every curated special drop (BDU Original Logo Tee + Cap, Rise II Power Black Cap). One-shot — safe to click on production."
        data-testid="merch-seed-specials-btn"
      >
        {busy ? "Working…" : "✨ Seed Special Drops"}
      </button>
      {lastResult && (
        <span className="text-xs text-bdu-cream/50 tabular-nums" data-testid="merch-repair-result">
          {lastResult.total_merchandise} total · {lastResult.active_after_repair} active
        </span>
      )}
    </div>
  );
}


function MerchTab() {
  const emptyForm = {
    name: "", description: "", category: "tshirt", brand: "bdu",
    price_dollars: "", image_url: "",
    sizes_csv: "S,M,L,XL,XXL",
    active: true, sort_order: 0,
  };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pfStatus, setPfStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Printful-link modal state
  const [linkingItem, setLinkingItem] = useState(null); // merch item currently being linked
  const [pfCatalog, setPfCatalog] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  const load = () => api.get("/merchandise", { params: { include_inactive: true } }).then((r) => setItems(r.data));
  const loadStatus = () => api.get("/admin/printful/status").then((r) => setPfStatus(r.data)).catch(() => {});
  useEffect(() => { load(); loadStatus(); }, []);

  const syncPrintful = async () => {
    if (!window.confirm("Pull products from Printful and import them into your merch catalog? Existing items keep their prices — only images and variant mapping refresh.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/printful/sync");
      toast.success(`Printful sync: ${data.imported} new · ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const priceCents = Math.round(parseFloat(form.price_dollars) * 100);
      if (!priceCents || priceCents <= 0) throw new Error("Enter a valid price greater than $0");
      const sizes = form.sizes_csv
        ? form.sizes_csv.split(",").map((s) => s.trim()).filter(Boolean)
        : null;
      const payload = {
        name: form.name,
        description: form.description,
        category: form.category,
        brand: form.brand || "bdu",
        price_cents: priceCents,
        image_url: form.image_url || null,
        sizes: (sizes && sizes.length > 0) ? sizes : null,
        active: !!form.active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingId) {
        await api.patch(`/merchandise/${editingId}`, payload);
        toast.success("Merch updated");
      } else {
        await api.post("/merchandise", payload);
        toast.success("Merch published");
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const edit = (m) => {
    setEditingId(m.id);
    setForm({
      name: m.name || "", description: m.description || "", category: m.category || "tshirt",
      brand: m.brand || "bdu",
      price_dollars: (Number(m.price_cents || 0) / 100).toFixed(2),
      image_url: m.image_url || "",
      sizes_csv: Array.isArray(m.sizes) ? m.sizes.join(",") : "",
      active: m.active !== false,
      sort_order: m.sort_order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try { await api.delete(`/merchandise/${id}`); toast.success("Merch removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (m) => {
    try {
      const payload = {
        name: m.name,
        description: m.description,
        category: m.category,
        brand: m.brand || "bdu",
        price_cents: m.price_cents,
        image_url: m.image_url || null,
        sizes: (m.sizes && m.sizes.length > 0) ? m.sizes : null,
        active: !m.active,
        sort_order: m.sort_order || 0,
      };
      await api.patch(`/merchandise/${m.id}`, payload);
      toast.success(m.active ? "Hidden from shop" : "Now live in shop");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const openLinkModal = async (m) => {
    setLinkingItem(m);
    setLinkLoading(true);
    try {
      const { data } = await api.get("/admin/printful/products");
      setPfCatalog(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Printful catalog");
      setLinkingItem(null);
    } finally {
      setLinkLoading(false);
    }
  };

  const linkTo = async (pfProductId) => {
    if (!linkingItem) return;
    setLinkLoading(true);
    try {
      const { data } = await api.post(
        `/admin/merchandise/${linkingItem.id}/link-printful`,
        { printful_product_id: pfProductId }
      );
      toast.success(`Linked · ${data.variants_linked} variant(s) · ready for auto-fulfillment`);
      setLinkingItem(null);
      setPfCatalog([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Link failed");
    } finally {
      setLinkLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold text-sm";

  return (
    <div className="space-y-6" data-testid="admin-merch-tab">
      {/* Printful integration status bar */}
      <div className="border border-bdu-gold/20 bg-bdu-surface p-5 flex items-center justify-between flex-wrap gap-3" data-testid="printful-status-bar">
        <div className="min-w-0">
          <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Print-on-Demand · Printful</div>
          <div className="mt-1 text-sm text-bdu-cream/80">
            {pfStatus === null ? (
              "Checking connection…"
            ) : pfStatus.configured ? (
              <span><span className="text-emerald-400">● Connected</span> — orders will auto-print & ship</span>
            ) : (
              <span><span className="text-amber-400">● Not connected</span> — set <span className="font-mono text-bdu-gold">PRINTFUL_API_KEY</span> to activate</span>
            )}
          </div>
        </div>
        <button
          onClick={syncPrintful}
          disabled={syncing || !pfStatus?.configured}
          className="btn-gold text-xs"
          data-testid="printful-sync-btn"
          title={!pfStatus?.configured ? "Add PRINTFUL_API_KEY first" : "Pull products from Printful"}
        >
          {syncing ? "Syncing…" : "Sync from Printful"}
        </button>
      </div>

      {/* Cheat sheet — how to launch a branded product */}
      <div className="border border-bdu-gold/20 bg-bdu-obsidian/40 p-6" data-testid="printful-cheatsheet">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Launch Playbook · 4 steps</div>
            <h4 className="mt-2 font-serif text-xl text-bdu-cream">From logo to live shop.</h4>
          </div>
          <a
            href="https://www.printful.com/dashboard/store"
            target="_blank"
            rel="noreferrer"
            className="text-xs uppercase tracking-widest text-bdu-gold hover:underline"
            data-testid="printful-dashboard-link"
          >
            Open Printful Dashboard →
          </a>
        </div>
        <ol className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { n: "1", t: "Design in Printful", b: "Add the BDU logo to a tee, cap, hoodie. Choose print placement + colors." },
            { n: "2", t: "Publish product", b: "Save & publish to your Manual/API store — Printful assigns a product ID." },
            { n: "3", t: "Sync here", b: "Click Sync from Printful ↑. New products import as hidden drafts." },
            { n: "4", t: "Rename + Activate", b: 'Click Edit on each item to swap the generic name (e.g. "Cotton Heritage MC1082") for your BDU brand name, then flip it Live.' },
          ].map((s) => (
            <li key={s.n} className="border border-bdu-gold/15 p-4">
              <div className="text-bdu-gold font-serif text-3xl leading-none">{s.n}</div>
              <div className="mt-2 text-sm font-semibold text-bdu-cream">{s.t}</div>
              <div className="mt-1 text-xs text-bdu-cream/60">{s.b}</div>
            </li>
          ))}
        </ol>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} className="bdu-card p-8 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-bdu-cream">{editingId ? "Edit Item" : "Add Merchandise"}</h3>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }} className="text-xs text-bdu-cream/60 hover:text-bdu-gold uppercase tracking-widest">Cancel edit</button>
          )}
        </div>
        <div><div className="label-eyebrow mb-3">Name</div>
          <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="admin-merch-name-input" /></div>
        <div><div className="label-eyebrow mb-3">Description</div>
          <textarea required rows={3} value={form.description} onChange={update("description")} className={inputCls} data-testid="admin-merch-description-input" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="label-eyebrow mb-3">Category</div>
            <select value={form.category} onChange={update("category")} className={inputCls} data-testid="admin-merch-category-input">
              {MERCH_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select></div>
          <div><div className="label-eyebrow mb-3">Brand Line</div>
            <select value={form.brand} onChange={update("brand")} className={inputCls} data-testid="admin-merch-brand-input">
              <option value="bdu">Black Dollars United</option>
              <option value="rise_ii_power">Rise II Power</option>
            </select></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="label-eyebrow mb-3">Price (USD)</div>
            <input type="number" min="1" step="0.01" required value={form.price_dollars} onChange={update("price_dollars")} placeholder="e.g., 35.00" className={inputCls} data-testid="admin-merch-price-input" /></div>
          <div />
        </div>
        <div><div className="label-eyebrow mb-3">Image URL</div>
          <input value={form.image_url} onChange={update("image_url")} placeholder="https://..." className={inputCls} data-testid="admin-merch-image-input" /></div>
        <div><div className="label-eyebrow mb-3">Sizes (comma-separated · leave empty for one-size)</div>
          <input value={form.sizes_csv} onChange={update("sizes_csv")} placeholder="S,M,L,XL,XXL" className={inputCls} data-testid="admin-merch-sizes-input" /></div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={update("active")} className="w-4 h-4 accent-bdu-gold" data-testid="admin-merch-active-input" />
            <span className="text-sm text-bdu-cream/80">Active (visible in shop)</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Sort Order</span>
            <input type="number" value={form.sort_order} onChange={update("sort_order")} className="w-20 bg-transparent border border-bdu-gold/25 px-2 py-1.5 text-bdu-cream text-sm outline-none focus:border-bdu-gold" />
          </div>
        </div>
        <button type="submit" disabled={submitting} className="btn-gold" data-testid="admin-merch-submit-btn">
          {submitting ? "Saving..." : editingId ? "Save Changes" : "Add to Shop"}
        </button>
      </form>

      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-serif text-2xl text-bdu-cream">All Merchandise</h3>
          <MerchRepairButtons onRepaired={load} />
        </div>
        {items.length === 0 ? (
          <div className="bdu-card text-bdu-cream/60">No merch yet.</div>
        ) : items.map((m, idx) => {
          const isPrintful = !!m.printful_product_id;
          const needsRename = isPrintful && !m.active; // heuristic: freshly synced + still hidden
          return (
          <div key={m.id} className={`bdu-card ${needsRename ? "border-bdu-gold/50" : ""}`} data-testid={`admin-merch-item-${m.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[0.6rem] uppercase tracking-widest text-bdu-gold/70 tabular-nums font-serif" data-testid={`merch-item-number-${m.id}`}>
                    #{String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
                    {m.category} · ${(m.price_cents / 100).toFixed(2)}
                  </span>
                  <span className={`text-[0.55rem] uppercase tracking-widest border px-1.5 py-0.5 ${m.brand === "rise_ii_power" ? "border-bdu-cream/40 text-bdu-cream/80" : "border-bdu-gold/50 text-bdu-gold"}`} data-testid={`merch-brand-badge-${m.id}`}>
                    {m.brand === "rise_ii_power" ? "Rise II Power" : "BDU"}
                  </span>
                  {isPrintful ? (
                    <span className="text-[0.55rem] uppercase tracking-widest border border-emerald-500/40 text-emerald-400 px-1.5 py-0.5" title={`Printful product ${m.printful_product_id}`} data-testid={`printful-badge-${m.id}`}>Printful ✓</span>
                  ) : (
                    <span className="text-[0.55rem] uppercase tracking-widest border border-amber-500/40 text-amber-400 px-1.5 py-0.5" title="No Printful mapping — orders need manual fulfillment">Manual</span>
                  )}
                  <span className={`text-[0.55rem] uppercase tracking-widest border px-1.5 py-0.5 ${m.active ? "border-emerald-500/40 text-emerald-400" : "border-bdu-cream/20 text-bdu-cream/50"}`}>
                    {m.active ? "Live" : "Hidden"}
                  </span>
                </div>
                <h4 className="mt-2 font-serif text-xl text-bdu-cream break-words">{m.name}</h4>
                <p className="mt-2 text-xs text-bdu-cream/60 line-clamp-2">{m.description}</p>
                {m.sizes && m.sizes.length > 0 && (
                  <div className="mt-2 text-xs text-bdu-cream/50">Sizes: {m.sizes.join(", ")}</div>
                )}
                {needsRename && (
                  <div className="mt-3 text-[0.7rem] text-bdu-gold/80 border-l-2 border-bdu-gold/60 pl-3">
                    Freshly synced — rename to your BDU brand name, then flip Live.
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  onClick={() => toggleActive(m)}
                  className={`text-xs uppercase tracking-widest hover:underline ${m.active ? "text-bdu-cream/70" : "text-emerald-400"}`}
                  data-testid={`admin-merch-toggle-${m.id}`}
                  title={m.active ? "Hide from shop" : "Publish to shop"}
                >
                  {m.active ? "Hide" : "Go Live"}
                </button>
                {!isPrintful && pfStatus?.configured && (
                  <button
                    onClick={() => openLinkModal(m)}
                    className="text-xs uppercase tracking-widest text-amber-400 hover:underline"
                    data-testid={`admin-merch-link-printful-${m.id}`}
                    title="Link this item to a Printful product so it auto-prints & ships"
                  >
                    Link Printful
                  </button>
                )}
                <button onClick={() => edit(m)} className="text-xs uppercase tracking-widest text-bdu-gold hover:underline" data-testid={`admin-merch-edit-${m.id}`}>Edit</button>
                <button onClick={() => remove(m.id)} className="text-xs uppercase tracking-widest text-bdu-red/80 hover:text-bdu-red" data-testid={`admin-merch-delete-${m.id}`}>Delete</button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
      </div>

      {/* Link-to-Printful modal */}
      {linkingItem && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => !linkLoading && setLinkingItem(null)}
          data-testid="printful-link-modal"
        >
          <div
            className="bg-bdu-obsidian border border-bdu-gold/30 max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Link to Printful</div>
                <h3 className="mt-2 font-serif text-2xl text-bdu-cream">Pick a Printful product for "{linkingItem.name}"</h3>
                <p className="mt-2 text-xs text-bdu-cream/60">Once linked, every paid order auto-routes to Printful for print + ship. Your name, price, and image stay exactly as they are.</p>
              </div>
              <button
                onClick={() => setLinkingItem(null)}
                className="text-bdu-cream/60 hover:text-bdu-red text-2xl leading-none"
                disabled={linkLoading}
                data-testid="printful-link-close"
              >×</button>
            </div>

            {linkLoading && pfCatalog.length === 0 ? (
              <div className="text-center py-10 text-bdu-cream/50">Loading Printful catalog…</div>
            ) : pfCatalog.length === 0 ? (
              <div className="text-center py-10 text-bdu-cream/60">
                No products in Printful yet. <a href="https://www.printful.com/dashboard/store" target="_blank" rel="noreferrer" className="text-bdu-gold hover:underline">Open Printful →</a> and design one first.
              </div>
            ) : (
              <div className="space-y-2">
                {pfCatalog.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => linkTo(p.id)}
                    disabled={linkLoading}
                    className="w-full text-left border border-bdu-gold/20 hover:border-bdu-gold p-3 flex items-center gap-3 transition disabled:opacity-40"
                    data-testid={`printful-link-option-${p.id}`}
                  >
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt="" className="w-16 h-16 object-cover bg-bdu-surface2 shrink-0" loading="lazy" />
                    ) : (
                      <div className="w-16 h-16 bg-bdu-surface2 shrink-0 flex items-center justify-center text-bdu-cream/30 text-xs">no img</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-bdu-cream font-medium truncate">{p.name}</div>
                      <div className="text-xs text-bdu-cream/50">Printful ID {p.id} · {p.variants} variant(s)</div>
                    </div>
                    <div className="text-xs uppercase tracking-widest text-bdu-gold shrink-0">Link →</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function AccountTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Please log in again.");
      localStorage.removeItem("bdu_token");
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-bdu-surface2 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition";

  return (
    <div className="max-w-lg">
      <div className="bdu-card">
        <div className="label-eyebrow mb-2">Security</div>
        <h3 className="font-serif text-3xl text-bdu-cream mb-2">Change Password</h3>
        <p className="text-bdu-cream/60 text-sm mb-6">
          Update the admin password. You will be logged out and required to sign in with the new password.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <label className="label-eyebrow block mb-2">Current Password</label>
            <input
              type="password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputCls}
              data-testid="current-password-input"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">New Password (min 8 characters)</label>
            <input
              type="password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputCls}
              data-testid="new-password-input"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Confirm New Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputCls}
              data-testid="confirm-password-input"
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="change-password-submit"
            className="w-full mt-2 px-6 py-3 bg-bdu-gold text-bdu-black uppercase tracking-widest text-xs font-semibold hover:bg-bdu-gold/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function FoundersTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [updateForm, setUpdateForm] = useState({ title: "", body: "" });

  const load = () => {
    setLoading(true);
    api.get(`/founders?status_filter=${filter}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/founders/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const rejectWithReason = async (id, name) => {
    const reason = window.prompt(
      `Reject ${name}'s pitch?\n\nOptional: write a short note explaining why (they'll receive this in the rejection email). Leave empty for a generic rejection.`,
      ""
    );
    if (reason === null) return; // cancelled
    try {
      await api.patch(`/admin/founders/${id}`, { status: "rejected", rejection_reason: reason.trim() });
      toast.success("Rejected — founder emailed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This removes them and all updates.`)) return;
    try {
      await api.delete(`/admin/founders/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const postUpdate = async (id) => {
    if (!updateForm.title || !updateForm.body) {
      toast.error("Title and body required");
      return;
    }
    try {
      await api.post(`/admin/founders/${id}/updates`, updateForm);
      toast.success("Update posted");
      setUpdateForm({ title: "", body: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`admin-founders-filter-${s}`}
            className={`px-3 py-1.5 border text-xs uppercase tracking-widest ${
              filter === s ? "border-bdu-gold text-bdu-gold" : "border-bdu-gold/20 text-bdu-cream/60"
            }`}
          >{s}</button>
        ))}
      </div>
      {loading ? (
        <div className="text-bdu-cream/50 py-10 text-center">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bdu-card text-center py-10 text-bdu-cream/60">No founders match this filter.</div>
      ) : (
        <div className="space-y-4">
          {items.map((f) => (
            <div key={f.id} className="bdu-card" data-testid={`admin-founder-${f.id}`}>
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="label-eyebrow">{f.category}</div>
                  <h3 className="mt-1 font-serif text-2xl text-bdu-cream">{f.business_name}</h3>
                  <div className="text-sm text-bdu-cream/60">by {f.name}{f.age ? `, ${f.age}` : ""} · {f.city}, {f.state}</div>
                  <div className="mt-2 text-xs text-bdu-cream/50">Goal: ${Number(f.goal_dollars).toLocaleString()} · Raised: ${Number(f.raised_dollars || 0).toLocaleString()}</div>
                </div>
                <span className={`text-[0.65rem] uppercase tracking-widest px-2 py-1 border ${
                  f.status === "approved" ? "border-emerald-500/50 text-emerald-400" :
                  f.status === "rejected" ? "border-bdu-red/50 text-bdu-red" :
                  "border-bdu-gold/50 text-bdu-gold"
                }`}>{f.status}</span>
              </div>
              <p className="mt-3 text-sm text-bdu-cream/70 line-clamp-3">{f.description}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                {f.status !== "approved" && <button onClick={() => update(f.id, { status: "approved" })} className="btn-gold text-xs" data-testid={`approve-founder-${f.id}`}>Approve</button>}
                {f.status !== "rejected" && <button onClick={() => rejectWithReason(f.id, f.name)} className="btn-ghost text-xs" data-testid={`reject-founder-${f.id}`}>Reject</button>}
                <button
                  onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                  className="px-3 py-2 text-xs uppercase tracking-widest text-bdu-cream/70 border border-bdu-gold/20 hover:border-bdu-gold hover:text-bdu-gold"
                  data-testid={`toggle-founder-${f.id}`}
                >
                  {expanded === f.id ? "Close" : "Post Update / Edit Raised"}
                </button>
                <button onClick={() => remove(f.id, f.name)} data-testid={`delete-founder-${f.id}`} className="px-3 py-2 text-xs uppercase tracking-widest text-bdu-red/80 hover:text-bdu-red border border-bdu-red/30 hover:border-bdu-red">Delete</button>
              </div>

              {expanded === f.id && (
                <div className="mt-6 pt-6 border-t border-bdu-gold/15 space-y-4">
                  <div>
                    <div className="label-eyebrow mb-2">Update Raised Amount ($)</div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        defaultValue={f.raised_dollars || 0}
                        className="bdu-input flex-1"
                        data-testid={`raised-input-${f.id}`}
                        id={`raised-${f.id}`}
                      />
                      <button
                        onClick={() => {
                          const val = parseInt(document.getElementById(`raised-${f.id}`).value);
                          update(f.id, { raised_dollars: val });
                        }}
                        className="btn-gold text-xs"
                        data-testid={`save-raised-${f.id}`}
                      >Save</button>
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-2">Post Journey Update</div>
                    <input
                      placeholder="Update title (e.g. Signed our lease!)"
                      value={updateForm.title}
                      onChange={(e) => setUpdateForm((s) => ({ ...s, title: e.target.value }))}
                      className="bdu-input mb-2"
                      data-testid={`update-title-${f.id}`}
                    />
                    <textarea
                      placeholder="Details of the milestone..."
                      rows={3}
                      value={updateForm.body}
                      onChange={(e) => setUpdateForm((s) => ({ ...s, body: e.target.value }))}
                      className="bdu-input"
                      data-testid={`update-body-${f.id}`}
                    />
                    <button
                      onClick={() => postUpdate(f.id)}
                      className="btn-gold text-xs mt-2"
                      data-testid={`post-update-${f.id}`}
                    >Post Update</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



const EMPTY_SPOTLIGHT = {
  name: "", title: "", business_name: "", quote: "", bio: "",
  hero_image_url: "", gallery_urls_text: "",
  website: "", phone: "", email: "", location: "",
  is_active: true, order: 100,
};

function SpotlightTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(EMPTY_SPOTLIGHT);

  const load = () => {
    setLoading(true);
    api.get("/spotlights?include_inactive=true")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY_SPOTLIGHT });
  };
  const startEdit = (item) => {
    setEditing(item.id);
    setForm({
      ...EMPTY_SPOTLIGHT,
      ...item,
      gallery_urls_text: (item.gallery_urls || []).join("\n"),
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_SPOTLIGHT); };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    const gallery = (form.gallery_urls_text || "")
      .split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    const payload = {
      name: form.name.trim(),
      title: form.title?.trim() || null,
      business_name: form.business_name?.trim() || null,
      quote: form.quote?.trim() || null,
      bio: form.bio?.trim() || null,
      hero_image_url: form.hero_image_url?.trim() || null,
      gallery_urls: gallery,
      website: form.website?.trim() || null,
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      location: form.location?.trim() || null,
      is_active: !!form.is_active,
      order: parseInt(form.order) || 0,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/spotlights", payload);
        toast.success("Spotlight created");
      } else {
        await api.patch(`/admin/spotlights/${editing}`, payload);
        toast.success("Spotlight updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/admin/spotlights/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}? This removes them from Faces of Black Excellence.`)) return;
    try {
      await api.delete(`/admin/spotlights/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-spotlight-tab">
      {/* LEFT: form */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow">
              {editing === "new" ? "Create Spotlight" : editing ? "Edit Spotlight" : "Compose"}
            </div>
            <h3 className="mt-2 font-serif text-3xl text-bdu-cream">Honor another Face.</h3>
          </div>
          {editing === null && (
            <button onClick={startNew} className="btn-gold text-xs" data-testid="spotlight-new-btn">+ New Honoree</button>
          )}
        </div>

        {editing !== null && (
          <div className="mt-6 space-y-4">
            <SField label="Name *" value={form.name} onChange={upd("name")} testId="spotlight-input-name" placeholder='Anthony "Spark Plug" Thomas' />
            <div className="grid sm:grid-cols-2 gap-4">
              <SField label="Title / Role" value={form.title} onChange={upd("title")} testId="spotlight-input-title" placeholder="Publisher & Owner" />
              <SField label="Business / Organization" value={form.business_name} onChange={upd("business_name")} testId="spotlight-input-business" placeholder="People You Need To Know" />
            </div>
            <STextarea rows={3} label="Featured Quote" value={form.quote} onChange={upd("quote")} testId="spotlight-input-quote" placeholder="A sentence or two that captures their voice…" />
            <STextarea rows={5} label="Bio / Story" value={form.bio} onChange={upd("bio")} testId="spotlight-input-bio" placeholder="The story of who they are and why they matter." />
            <SField label="Hero Image URL" value={form.hero_image_url} onChange={upd("hero_image_url")} testId="spotlight-input-hero" placeholder="https://…" />
            <STextarea rows={3} label="Gallery Image URLs (one per line)" value={form.gallery_urls_text} onChange={upd("gallery_urls_text")} testId="spotlight-input-gallery" placeholder={"https://.../cover1.jpg\nhttps://.../cover2.jpg"} />
            <div className="grid sm:grid-cols-2 gap-4">
              <SField label="Website" value={form.website} onChange={upd("website")} testId="spotlight-input-website" placeholder="https://sparkplug.com" />
              <SField label="Phone" value={form.phone} onChange={upd("phone")} testId="spotlight-input-phone" placeholder="770-460-5995" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <SField label="Email" value={form.email} onChange={upd("email")} testId="spotlight-input-email" placeholder="hello@example.com" />
              <SField label="Location" value={form.location} onChange={upd("location")} testId="spotlight-input-location" placeholder="Atlanta, GA" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 text-sm text-bdu-cream/80 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  data-testid="spotlight-input-active"
                />
                <span>Active (visible on site)</span>
              </label>
              <SField
                label="Display Order (higher = featured)"
                type="number"
                value={form.order}
                onChange={upd("order")}
                testId="spotlight-input-order"
                placeholder="100"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={save} className="btn-gold" data-testid="spotlight-save-btn">
                {editing === "new" ? "Publish" : "Save Changes"}
              </button>
              <button onClick={cancel} className="btn-ghost" data-testid="spotlight-cancel-btn">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: list */}
      <div>
        <div className="label-eyebrow">Current Roster</div>
        <h3 className="mt-2 font-serif text-3xl text-bdu-cream">The Wall.</h3>
        {loading ? (
          <div className="mt-8 text-bdu-cream/50">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-8 text-bdu-cream/40 text-sm">No honorees yet. Create the first one on the left.</div>
        ) : (
          <ul className="mt-6 space-y-4">
            {items.map((it) => (
              <li key={it.id} className="border border-bdu-gold/15 bg-bdu-surface p-4 flex gap-4 items-start" data-testid={`spotlight-item-${it.id}`}>
                {it.hero_image_url ? (
                  <img src={it.hero_image_url} alt={it.name} className="w-20 h-24 object-cover shrink-0 border border-bdu-gold/20" />
                ) : (
                  <div className="w-20 h-24 shrink-0 border border-bdu-gold/15 flex items-center justify-center text-bdu-cream/30 font-serif text-2xl">
                    {it.name?.[0] || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-lg text-bdu-cream truncate">{it.name}</span>
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${it.is_active ? "border-emerald-500/40 text-emerald-400" : "border-bdu-cream/20 text-bdu-cream/40"}`}>
                      {it.is_active ? "Active" : "Hidden"}
                    </span>
                    <span className="text-[10px] text-bdu-cream/40">order {it.order}</span>
                  </div>
                  {it.title && <div className="text-xs text-bdu-gold uppercase tracking-widest">{it.title}</div>}
                  {it.business_name && <div className="text-xs text-bdu-cream/60 italic mt-0.5 truncate">{it.business_name}</div>}
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                    <button onClick={() => startEdit(it)} className="btn-ghost text-[10px] py-1 px-2" data-testid={`spotlight-edit-${it.id}`}>Edit</button>
                    <button onClick={() => toggleActive(it)} className="btn-ghost text-[10px] py-1 px-2" data-testid={`spotlight-toggle-${it.id}`}>
                      {it.is_active ? "Hide" : "Show"}
                    </button>
                    <button onClick={() => remove(it)} className="text-bdu-red hover:underline text-[10px] py-1 px-2" data-testid={`spotlight-delete-${it.id}`}>
                      <Trash2 size={11} className="inline" /> Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2">{label}</div>
      <input type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder} className="bdu-input" data-testid={testId} />
    </label>
  );
}

function STextarea({ label, value, onChange, rows = 3, placeholder, testId }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2">{label}</div>
      <textarea rows={rows} value={value ?? ""} onChange={onChange} placeholder={placeholder} className="bdu-input" data-testid={testId} />
    </label>
  );
}
